import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { useRequestStatus } from 'hooks/useRequestStatus';
import CloseIcon from 'svg/CloseIcon';
import Minus from 'svg/Minus';
import PlusPlain from 'svg/PlusPlain';
import Button, { ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import { cropImageFile } from './cropImageFile';

export type CropShape = 'round' | 'rect';

export interface ImageCropperOptions {
  /** Width / height of the crop, e.g. 1 for avatars, 3 for a cover. */
  aspectRatio?: number;
  /** Round only changes the mask; the exported file is still rectangular. */
  shape?: CropShape;
  title?: string;
}

interface Props extends ImageCropperOptions {
  isOpen: boolean;
  /** Forwarded to Modal so a freshly mounted instance still animates in. */
  appear?: boolean;
  file: File;
  onCancel: () => void;
  onApply: (file: File) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.05;
const ZOOM_BUTTON_STEP = ZOOM_STEP * 4;
const CENTER: Point = { x: 0, y: 0 };

export default function ImageCropperModal({
  isOpen,
  file,
  aspectRatio = 1,
  shape = 'rect',
  title = 'Crop photo',
  appear,
  onCancel,
  onApply,
}: Props) {
  const imageUrl = useMemo(() => URL.createObjectURL(file), [file]);
  const [crop, setCrop] = useState<Point>(CENTER);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [cropArea, setCropArea] = useState<Area | null>(null);
  const { isLoading: isApplying, setLoading, setSuccess, setError, error } = useRequestStatus();

  useEffect(() => () => URL.revokeObjectURL(imageUrl), [imageUrl]);

  const handleApply = async () => {
    if (!cropArea) return;
    setLoading();
    try {
      const cropped = await cropImageFile(file, cropArea);
      setSuccess();
      onApply(cropped);
    } catch (caught) {
      Sentry.captureException(caught);
      setError('Could not crop this photo, please try again');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onCancel}
      appear={appear}
      classNameMaxWidth="sm:max-w-[35rem]"
    >
      <div className="flex items-center justify-between border-b border-color-border-input-lightmode px-ds-xl py-ds-lg dark:border-color-border-input-darkmode">
        <div className="flex items-center gap-ds-md">
          <ButtonText onClick={onCancel} aria-label="Close">
            <CloseIcon className="h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
          </ButtonText>
          <h2 className="typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {title}
          </h2>
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          isInline
          loading={isApplying}
          disabled={!cropArea}
          onClick={() => void handleApply()}
          className="min-w-[5.5rem]"
        >
          Apply
        </Button>
      </div>

      {/* Cropper is position:absolute, so it fills this box. Always-black letterbox + always-white frame. */}
      <div className="relative h-[22rem] w-full bg-color-bg-lightmode-invert sm:h-[26rem]">
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          cropShape={shape}
          showGrid={false}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          // Not onCropComplete: after a slider zoom it can report the pre-clamp position.
          onCropAreaChange={(_area, areaPixels) => setCropArea(areaPixels)}
          classes={{ cropAreaClassName: '!border-[3px] !border-color-bg-darkmode-invert' }}
        />
      </div>

      <div className="flex items-center gap-ds-lg border-t border-color-border-input-lightmode px-ds-xl py-ds-lg dark:border-color-border-input-darkmode">
        <Button
          type="button"
          variant="secondary"
          size="icon"
          isInline
          aria-label="Zoom out"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => setZoom(Math.max(MIN_ZOOM, zoom - ZOOM_BUTTON_STEP))}
          className="h-8 w-8 shrink-0 lg:w-8"
        >
          <Minus className="h-5 w-5" />
        </Button>
        <input
          type="range"
          aria-label="Zoom"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={ZOOM_STEP}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          className="h-1 w-full cursor-pointer appearance-none rounded-full bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5"
        />
        <Button
          type="button"
          variant="secondary"
          size="icon"
          isInline
          aria-label="Zoom in"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => setZoom(Math.min(MAX_ZOOM, zoom + ZOOM_BUTTON_STEP))}
          className="h-8 w-8 shrink-0 lg:w-8"
        >
          <PlusPlain className="h-5 w-5" />
        </Button>
      </div>

      {error && (
        <p className="typography-product-caption px-ds-xl pb-ds-lg text-color-text-lightmode-error dark:text-color-text-darkmode-error">
          {error}
        </p>
      )}
    </Modal>
  );
}
